import { ErrorHandler, Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationService } from '@services/notification.service';
import { AuthService } from '@services/auth.service';
import { PhoneInfoService } from '@avatar/services/phone-info.service';
import { TelegramService } from '@services/telegram.service';
import { StoreErrorHandlerService } from './store-error-handler.service';
import { WebSocketController } from '@services/web-socket.controller';
import { environment } from '@environments/environment';
import { AppInfoService } from '@services/app-info.service';

interface ErrorMassage {
  environment: string,
  timestamp: string,
  userIP?: string,
  appVersion: string,
  userID: number | string,
  device: string,
  deviceID: number | string,
  socketId: string,
  socketConnected: boolean | string,
  message: string,
  errorStack?: string,
}

@Injectable({
  providedIn: 'root'
})
export class CustomErrorHandler implements ErrorHandler {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly notificationService = inject(NotificationService);
  private readonly phoneInfoService = inject(PhoneInfoService);
  private readonly telegramService = inject(TelegramService);
  private readonly storeErrorHandlerService = inject(StoreErrorHandlerService);
  private readonly webSocketController = inject(WebSocketController);
  private readonly appInfoService = inject(AppInfoService);

  private readonly production: boolean = false;

  constructor() {
    this.production = environment.production;
  }

  public async throwError(error: any): Promise<void> {
    await this.handleError(error);
  }

  public async handleError(error: any): Promise<void> {
    this.storeErrorHandlerService.addError(error);

    if (environment.useSentry) {
      const payload: ErrorMassage = await this.createMessage(error);
      await this.telegramService.sendMessage(payload);
    } else {
      console.error(error);
    }
  }

  private async createMessage(error: any): Promise<ErrorMassage> {
    const environment: string = this.production ? 'Production' : 'Development';
    const timestamp: string = new Date().toISOString();
    // const publicIP: any = await firstValueFrom(this.httpClient.get('https://api.ipify.org?format=json'));
    // const userIP: string = publicIP.ip;
    const appVersion: string = 'WEB';
    const userID: number | string = this.authService.parsedToken?.userId || 'User does not authorized';
    const device: string = await this.phoneInfoService.getDeviceModelWithOS() || 'Device model is unavailable';
    const deviceID: number | string = this.notificationService.getUserDeviceId() || 'Has not been retrieved from the backend';
    const socketId: string = this.webSocketController.socketId || 'Socket ID is unavailable';
    const socketConnected: boolean | string = this.webSocketController.socketConnected || 'Socket connection status is unavailable';
    const message: string = error.message || (typeof error.toString() == 'function' ?? error.toString()) || 'The error message is unavailable';
    const errorStack: string = error.stack || 'The call stack is unavailable';

    await this.checkChunkLoadError(message);

    const newMessage: ErrorMassage = {
      environment,
      timestamp,
      // userIP,
      appVersion,
      userID,
      device,
      deviceID,
      socketId,
      socketConnected,
      message,
    };

    if (message.length < 70) {
      newMessage.errorStack = errorStack;
    }

    newMessage.appVersion = await this.appInfoService.getFullAppVersion();

    return newMessage;
  }

  private async checkChunkLoadError(message: string): Promise<void> {
    if (message && message.includes('ChunkLoadError')) {
      console.error('Chunk loading error, refreshing the app.', message);
      await this.router.navigate(['/']);
    }
  }
}

